import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export function getBaseUrl() {
    if (typeof window !== 'undefined') {
        // Client-side
        return window.location.origin;
    }
    // Server-side
    if (process.env.NEXT_PUBLIC_APP_URL) {
        return process.env.NEXT_PUBLIC_APP_URL;
    }
    // Fallback for server-side if NEXT_PUBLIC_APP_URL is not set (e.g., local dev without it)
    return `http://localhost:${process.env.PORT || 3000}`;
}

/**
 * Generates a comprehensive cigar name from brand, model, and size.
 * Handles cases where parts are missing and avoids duplicating brand name if already in the model.
 * @param brand - The brand of the cigar.
 * @param model - The model or line name of the cigar.
 * @param size - The size or vitola of the cigar (e.g., "Robusto", "5x50").
 * @returns A formatted cigar name string, e.g., "Brand Model Size", or a fallback.
 */
export function generateComprehensiveCigarName(
    brand?: string | null,
    model?: string | null,
    size?: string | null
): string {
    const parts: string[] = [];
    const normalizedBrand = brand?.trim();
    const normalizedModel = model?.trim();
    const normalizedSize = size?.trim();

    if (normalizedBrand) {
        if (normalizedModel && normalizedModel.toLowerCase().startsWith(normalizedBrand.toLowerCase())) {
            // Model already contains the brand, so just use model
            parts.push(normalizedModel);
        } else {
            parts.push(normalizedBrand);
            if (normalizedModel) {
                parts.push(normalizedModel);
            }
        }
    } else if (normalizedModel) {
        parts.push(normalizedModel);
    }

    if (normalizedSize) {
        parts.push(normalizedSize);
    }

    if (parts.length === 0) {
        return 'Unknown Cigar'; // Fallback if all parts are missing
    }

    return parts.join(' ').trim();
}
